import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription, Subject, of } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { SecureStorageService } from './core/validators/secure-storage.service';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { LoadingConst } from './core/constants/color-loading';
import { ParametersService } from './core/services/parameters.service';
import { IRequest } from './core/interfaces/parameters';

const PrimaryColor = LoadingConst.colorPrimary;
const SecondaryColor = LoadingConst.colorSecondary;

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'MEDIANET_ONB_FE';
  IsInit: boolean = false;
  private destroy$ = new Subject<void>();

  public primaryColour = PrimaryColor;
  public secondaryColour = SecondaryColor;
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private secureStorageService: SecureStorageService,
    private parametersService: ParametersService,
  ){}

  ngOnInit() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        window.scrollTo(0, 0);
      });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleNavigation();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async handleNavigation() {
    const rutaActual = this.router.url;

    if (rutaActual === '/onboarding/index') {
      this.clearStorage();
      this.getParameters({ CompanyId: this.secureStorageService.getData('CompanyId') || "default" }).subscribe();
    } else if (rutaActual === '/onboarding/index/step-1') {
      const data = JSON.parse(this.secureStorageService.getData('designConfig') || "false");
      if (!data) {
        await this.getParameters({ CompanyId: 'default' }).subscribe();
      } else {
        this.applyConfigurations(data);
        this.IsInit = true;
        this.cdr.detectChanges();
      }
    } else {
      this.activatedRoute.queryParams.pipe(
        takeUntil(this.destroy$),
        switchMap(queryParams => {
          if (queryParams['access_key']) {

            //? DESENCRIPTAR COMPAÑIA
            const decodedBase64 = this.secureStorageService.decryptBase64(queryParams['access_key']);
            const safeResponse = this.secureStorageService.decrypt(decodedBase64.replace(/"/g, ''));

            return this.getParameters({ CompanyId: safeResponse });
          } else {
            const datos = JSON.parse(this.secureStorageService.getData('designConfig') || "false");
            if (datos) {
              this.applyConfigurations(datos);
            }
            this.IsInit = true;
            this.cdr.detectChanges();
            return of(null);
          }
        })
      ).subscribe();
    }
  }

  private clearStorage() {
    //this.secureStorageService.removeData("designConfig");
    this.secureStorageService.removeData("IdTracking");
    //this.secureStorageService.removeData("CompanyId");
    this.secureStorageService.removeData("biometria_id");
    this.secureStorageService.removeData("negocio");
  }

  getParameters(request: IRequest) {
    return this.parametersService.getParameters(request).pipe(
      takeUntil(this.destroy$),
      switchMap(data => {
        if (data?.Succeeded) {
          const companyId = data.Data.CompanyId;
          const parametros = data.Data.Parameters;
          const documentStep = data.Data.DocumentStep;

          if (companyId) {
            this.secureStorageService.saveData("CompanyId", companyId);
            this.secureStorageService.saveData("DocumentStep", documentStep.toString());
          } else {
            this.navigateToMaintenance();
          }

          if (parametros) {
            this.secureStorageService.saveData("designConfig", JSON.stringify(parametros));
            this.applyConfigurations(parametros);
          } else {
            this.navigateToMaintenance();
          }
        } else {
          this.navigateToMaintenance();
        }
        return of(null);
      })
    );
  }

  private navigateToMaintenance() {
    this.router.navigate(['/error/MaintenancePage']).then(() => {
      this.cdr.detectChanges();
    });
  }

  convertArrayToObject(array: any) {
    return array.reduce((result: any, item: any) => {
      result[item.Parametro] = item.Valor;
      return result;
    }, {});
  }

  applyConfigurations(config: any) {
    if (!config) return;
    document.documentElement.style.setProperty('--color-primario', config.COLOR_TEMA_PRIMARIO);
    document.documentElement.style.setProperty('--color-secundario', config.COLOR_TEMA_SECUNDARIO);
    document.documentElement.style.setProperty('--color-terceario', config.COLOR_TEMA_TERCIARIO);
    this.changeFavicon(config.LOGO_PRIMARIO);
    this.IsInit = true;
    this.cdr.detectChanges();
  }

  private changeFavicon(iconUrl: string): void {
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = iconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  }
}
